/** @jsx jsx */
import React, { useMemo } from 'react';
import { css, jsx, styled } from '@compiled/react';
import { graphql, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import {
	NavigationHeader,
	NavigationContent,
	Section,
	SideNavigation,
} from '@atlaskit/side-navigation';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { StarredFiltersList } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/starred-filters-list/index.tsx';
import { TitleHeader } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/title-header/main.tsx';
import { ContextualAnalyticsData } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/contextual-analytics-data/index.tsx';
import type { menuNavigationAppsSidebarFiltersGlobalScopeQuery } from '@atlassian/jira-relay/src/__generated__/menuNavigationAppsSidebarFiltersGlobalScopeQuery.graphql';
import { useIsActiveRoute } from '@atlassian/react-resource-router';
import { MENU_ID } from '../../../common/constants/index.tsx';
import { messages } from './messages.tsx';
import { SystemFiltersSection } from './system-filters/index.tsx';

// need to reference from parent component for the QueryLoader, tangerine won't allow direct import from the generated files.

const analyticsAttributes = {
	menu: {
		navigatorScope: 'global',
	},
	searchIssues: { itemId: 'search-issues' },
	viewAllFilters: { itemId: 'view-all-filters' },
} as const;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const searchIssuesSelectedOn = (currentLocation: any) =>
	('jql' in currentLocation.query && !currentLocation.query.filter) ||
	(Boolean(currentLocation.pathname.match(/\/issues(\/|\/([A-Z]+)-(\d+))?$/i)) &&
		!currentLocation.query.jql &&
		!currentLocation.query.filter);

type Props = {
	queryReference: PreloadedQuery<menuNavigationAppsSidebarFiltersGlobalScopeQuery>;
	onFavouritesChanged: () => void;
};

const sectionCustomCssExperiment = {
	"&&& > div > div[data-ds--menu--heading-item='true']": {
		// TODO Delete this comment after verifying space token -> previous value `'6px'`
		paddingLeft: token('space.075'),
		color: token('color.text'),
	},
};

const topMarginCustomCssExperiment = {
	'> div[data-section="true"]': {
		marginTop: token('space.negative.075'),
	},
};

export const Menu = ({ queryReference, onFavouritesChanged }: Props) => {
	const { formatMessage } = useIntl();

	const isIssueNavigatorSSRPlaceholderEnabled = useIsActiveRoute('global-issue-navigator');

	const isIssueViewSSRPlaceholderEnabled = useIsActiveRoute('issue');

	const isSSRPlaceholderEnabled =
		isIssueNavigatorSSRPlaceholderEnabled || isIssueViewSSRPlaceholderEnabled;

	const data = usePreloadedQuery<menuNavigationAppsSidebarFiltersGlobalScopeQuery>(
		graphql`
			query menuNavigationAppsSidebarFiltersGlobalScopeQuery($cloudId: ID!) {
				jira @required(action: THROW) {
					...starredFiltersList_navigationAppsSidebarCommon @arguments(cloudId: $cloudId)
					...systemFilters_navigationAppsSidebarFilters_SystemFiltersSection
						@arguments(cloudId: $cloudId)
				}
			}
		`,
		queryReference,
	);

	const systemFiltersSection = useMemo(
		() => (
			// TODO remove css prop and wrapper once @atlaskit/section design gets updated
			// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			<div css={[css(sectionCustomCssExperiment)]}>
				<SystemFiltersSection baseHref="/issues/" jiraQuery={data.jira} />
			</div>
		),
		[data.jira],
	);

	const starredFiltersList = useMemo(
		() => (
			// TODO remove css prop and wrapper once @atlaskit/section design gets updated
			<div
				css={[
					// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values
					css({
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
						...sectionCustomCssExperiment,
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
						...topMarginCustomCssExperiment,
					}),
				]}
			>
				<StarredFiltersList
					baseHref="/issues/"
					jiraQuery={data.jira}
					onFavouritesChanged={onFavouritesChanged}
				/>
			</div>
		),
		[data.jira, onFavouritesChanged],
	);

	return (
		<ContextualAnalyticsData menuId={MENU_ID} attributes={analyticsAttributes.menu}>
			<SideNavigation
				label=""
				isServer={__SERVER__}
				isSSRPlaceholderEnabled={isSSRPlaceholderEnabled}
			>
				<NavigationHeader>
					<TitleHeader>{formatMessage(messages.filtersHeader)}</TitleHeader>
				</NavigationHeader>
				<NavigationContent>
					<Section>
						<MenuItem
							analytics={analyticsAttributes.searchIssues}
							selectedOn={searchIssuesSelectedOn}
							href="/issues/?jql=order+by+created+DESC"
						>
							{formatMessage(messages.newSearch)}
						</MenuItem>
					</Section>
					<DividerLine />
					<>
						{systemFiltersSection}
						{starredFiltersList}
					</>
					<Section hasSeparator>
						<MenuItem analytics={analyticsAttributes.viewAllFilters} href="/jira/filters">
							{formatMessage(messages.viewAllFilters)}
						</MenuItem>
					</Section>
				</NavigationContent>
			</SideNavigation>
		</ContextualAnalyticsData>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const DividerLine = styled.div({
	borderTop: `2px solid ${token('color.border')}`,
});
